/**
 * Device Operations Table - Frontend
 * @description Table component for displaying device operations with modern UI
 * Follows the same pattern as DeviceDashboardTable.tsx
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';

// UI Components from the main system
import { TableHeader } from '@/components/shared/tables/TableHeader/TableHeader';
// import { TableButton } from '@/components/ui/TableButton';
import { FilterDialog } from '@/components/shared/FilterDialog';

// Hooks from the main system
import { useTableFilters } from '@/hooks/useTableFilters';

// Configuration
import { getDeviceOperationsTableColumns } from '@/constants/tables/deviceOperationsTable.config';

// Table cells
import { DeviceOperationsActionsCell } from './deviceOperationsTableCells/DeviceOperationsActionsCell';
import { CellContext } from '@tanstack/react-table';
import { DashboardOperation } from '@/types/dashboard.types';

interface DeviceOperationsTableProps {
  operations: DashboardOperation[];
  loading?: boolean;
  deviceId?: string;
  onCenterMap: (operationId: string) => void;
  onViewLectures: (deviceId: string, operationId: string) => void;
  selectedRowId?: string | null;
  onRowClick?: (operationId: string) => void;
}

export const DeviceOperationsTable: React.FC<DeviceOperationsTableProps> = ({
  operations,
  loading = false,
  deviceId,
  onCenterMap,
  onViewLectures,
  selectedRowId,
  onRowClick,
}) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'startTimestamp', desc: true },
  ]);

  // Get column configuration
  const columnConfigs = useMemo(() => getDeviceOperationsTableColumns(), []);

  const {
    filterState,
    columnFilters,
    openFilter,
    closeFilter,
    setSearch,
    toggleOption,
    clearFilter,
    applyFilter,
    getFilteredOptions,
  } = useTableFilters(operations, columnConfigs);

  const columnHelper = createColumnHelper<DashboardOperation>();

  // Build columns from config + actions
  const columns: ColumnDef<DashboardOperation>[] = useMemo(
    () => [
      ...columnConfigs.map((config) =>
        columnHelper.accessor(config.accessor, {
          id: config.id,
          header: ({ column }) => {
            const filterValue = column.getFilterValue() as string[] | undefined;
            const isFiltered = Array.isArray(filterValue) && filterValue.length > 0;
            return (
              <TableHeader
                header={column}
                columnConfig={config}
                onFilterClick={(columnId) => openFilter(columnId, columnFilters)}
                isFiltered={isFiltered}
              />
            );
          },
          cell:
            config.cell ||
            (({ getValue }) => {
              const value = getValue();
              return <span className="text-gray-900">{value?.toString() || '-'}</span>;
            }),
          enableSorting: config.enableSorting,
          enableColumnFilter: config.enableFilter,
          filterFn: config.enableFilter
            ? (row, _columnId, filterValue) => {
                if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
                const value = row.getValue(_columnId);
                const stringValue = value?.toString() || '';

                // Convertir filterValue a strings para comparación
                const stringFilterValues = filterValue.map((v) => v?.toString() || '');

                // Debug logs
                if (config.id === 'lectureCount') {
                  console.log('Filtering lectureCount:', {
                    columnId: _columnId,
                    filterValue,
                    stringFilterValues,
                    cellValue: value,
                    stringValue,
                    includes: stringFilterValues.includes(stringValue),
                  });
                }

                // Para filtros de tipo select (incluye números y texto)
                return stringFilterValues.includes(stringValue);
              }
            : undefined,
        })
      ),
      // Actions column
      columnHelper.display({
        id: 'actions',
        header: t('pages.operations.columns.actions'),
        cell: ({ row }) => {
          return (
            <DeviceOperationsActionsCell
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              info={{ row, getValue: () => null } as CellContext<DashboardOperation, any>}
              actions={{
                onCenterMap,
                onViewLectures,
              }}
              deviceId={deviceId}
            />
          );
        },
      }),
    ],
    [
      columnConfigs,
      columnHelper,
      t,
      openFilter,
      columnFilters,
      onCenterMap,
      onViewLectures,
      deviceId,
    ]
  );

  const table = useReactTable({
    data: operations,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleRowClick = (operationId: string) => {
    if (onRowClick) {
      onRowClick(operationId);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg text-gray-600">{t('pages.operations.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Filter Dialogs */}
      {columnConfigs
        .filter((config) => config.enableFilter)
        .map((config) => {
          const filterTitleKey = `pages.operations.filters.${config.id}`;
          return (
            <FilterDialog
              key={config.id}
              isOpen={!!filterState[config.id]?.isOpen}
              onClose={() => closeFilter(config.id)}
              title={t(filterTitleKey)}
              searchValue={filterState[config.id]?.search || ''}
              onSearchChange={(value) => setSearch(config.id, value)}
              options={getFilteredOptions(config.id)}
              selectedOptions={filterState[config.id]?.selected || []}
              onOptionToggle={(option) => toggleOption(config.id, option)}
              onClear={() => clearFilter(config.id)}
              onApply={() => applyFilter(config.id)}
            />
          );
        })}

      {/* Header with actions */}
      {/*
      <div className="px-1 py-1 border-b border-gray-200 flex justify-between items-center">
         <div>
          <h3 className="text-lg font-medium text-gray-900">{t('pages.operations.title')}</h3>
          <p className="text-sm text-gray-500">
            {t('pages.operations.subtitle', { count: operations.length })}
          </p>
        </div> 
        <div className="flex space-x-2">
          {onRefresh && (
            <TableButton
              permission={PERMISSIONS.OPERATIONAL_READ}
              onClick={onRefresh}
              icon={<RefreshCw className="h-4 w-4" />}
              label={t('common.refresh')}
              variant="secondary"
            />
          )}
          {onExport && (
            <TableButton
              permission={PERMISSIONS.OPERATIONAL_READ}
              onClick={() => onExport('excel')}
              icon={<FileSpreadsheet className="h-4 w-4" />}
              label={t('common.export')}
              variant="secondary"
            />
          )}
          {showAdvancedExport && (
            <TableButton
              permission={PERMISSIONS.OPERATIONAL_READ}
              onClick={handleAdvancedExport}
              icon={<FileSpreadsheet className="h-4 w-4" />}
              label={t('common.exportAdvanced')}
              variant="primary"
            />
          )}
        </div>
      </div>
      */}

      {/* Search input */}
      {/*
      <div className="px-4 py-2 border-b border-gray-200">
        <input
          type="text"
          placeholder={t('pages.operations.search')}
          className="px-4 py-2 border border-gray-300 rounded-md w-full"
          value={(table.getColumn('operationIdentifier')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('operationIdentifier')?.setFilterValue(e.target.value)}
        />
      </div>
      */}

      {/* Table Container with scroll */}
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)' }}>
          <table className="w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-brand-gray-dark">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-1 py-1 text-left text-xs font-medium text-brand-white tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-row-id={row.original.id}
                  className={`text-xs transition-colors duration-200 cursor-pointer ${
                    selectedRowId === row.original.id
                      ? 'bg-orange-50 hover:bg-orange-100 border-l-4 border-orange-400'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleRowClick(row.original.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-1 py-1 whitespace-nowrap text-xs text-gray-900">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty state */}
      {operations.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">{t('pages.operations.empty.title')}</div>
          <p className="text-gray-500 text-sm">{t('pages.operations.empty.description')}</p>
        </div>
      )}
    </div>
  );
};
